import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { IDimensionTogglesProps, DimensionKey, IEnabledDimensions } from '@/@types/ai-studio';
import { DIMENSION_KEYS, getDimensionLabel, countEnabledDimensions } from '@/@types/ai-studio';

export function DimensionToggles({
  enabledDimensions,
  onDimensionsChange,
  disabled = false,
}: IDimensionTogglesProps) {
  const enabledCount = countEnabledDimensions(enabledDimensions);

  const handleToggle = (key: DimensionKey) => {
    const isCurrentlyEnabled = enabledDimensions[key];

    // Don't allow disabling all dimensions
    if (isCurrentlyEnabled && enabledCount <= 1) {
      return;
    }

    onDimensionsChange({
      ...enabledDimensions,
      [key]: !isCurrentlyEnabled,
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">Dimensions</span>
        <span className="text-xs text-muted-foreground">
          {enabledCount}/{DIMENSION_KEYS.length} enabled
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {DIMENSION_KEYS.map((key) => {
          const isEnabled = enabledDimensions[key as keyof IEnabledDimensions];
          const isLastEnabled = isEnabled && enabledCount === 1;

          return (
            <label
              key={key}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 transition-colors',
                disabled || isLastEnabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer hover:bg-muted/50'
              )}
            >
              <Checkbox
                checked={isEnabled}
                onCheckedChange={() => handleToggle(key)}
                disabled={disabled || isLastEnabled}
              />
              <span className="text-sm text-foreground">{getDimensionLabel(key)}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
